import type { Channel, ChannelType } from "sdk";
import { createMemo, Match, Show, Switch } from "solid-js";
import { useApi } from "@/api";
import icChanBroadcast1 from "@/assets/camera.png";
import icChanCalendar1 from "@/assets/channel-calendar-1.png";
import icChanDocument1 from "@/assets/channel-document-1.png";
import icChanForum1 from "@/assets/channel-forum-1.png";
import icChanForum2 from "@/assets/channel-forum2.png";
import icChanInfo1 from "@/assets/channel-info-1.png";
import icChanNsfw from "@/assets/channel-nsfw.png";
import icChanScripts1 from "@/assets/channel-scripts-1.png";
import icChanText1 from "@/assets/channel-text-1.png";
import icChanText2 from "@/assets/channel-text-2.png";
import icChanText3 from "@/assets/channel-text-3.png";
import icChanText4 from "@/assets/channel-text-4.png";
import icChanThreadPublic from "@/assets/channel-thread.png";
import icChanThreadPrivate from "@/assets/channel-thread-private.png";
import icChanTicket1 from "@/assets/channel-ticket-1.png";
import icChanVoice1 from "@/assets/channel-voice-1.png";
import icChanVoice2 from "@/assets/channel-voice-2.png";
import icChanWiki1 from "@/assets/channel-wiki-1.png";
import icChanCategory1 from "@/assets/folder-1.png";
import icChanUnknown from "@/assets/question.png";
import { useCurrentUser } from "@/contexts/currentUser.tsx";
import { getColor } from "@/lib/colors";
import { cyrb53, LCG } from "@/lib/rng";
import { getThumbFromId } from "@/media/util";
import { AvatarWithStatus } from "./UserAvatar";

export const channelIcon = (channelType: ChannelType, seed: string) => {
	const rand = LCG(cyrb53(seed));
	function rnd<T>(arr: T[]): T {
		return arr[Math.floor(rand() * arr.length)];
	}
	switch (channelType) {
		case "Voice":
			return rnd([icChanVoice1, icChanVoice2]);
		case "Broadcast":
			return rnd([icChanBroadcast1]);
		case "Forum":
			return rnd([icChanForum1]);
		case "Forum2":
			return rnd([icChanForum2]);
		case "Ticket":
			return rnd([icChanTicket1]);
		case "ThreadPublic":
		case "ThreadForum2":
			return rnd([icChanThreadPublic]);
		case "ThreadPrivate":
			return rnd([icChanThreadPrivate]);
		case "Calendar":
			return rnd([icChanCalendar1]);
		case "Document":
			return rnd([icChanDocument1]);
		case "Wiki":
			return rnd([icChanWiki1]);
		case "Scripts":
			return rnd([icChanScripts1]);
		case "Category":
			return rnd([icChanCategory1]);
		case "Info":
			return rnd([icChanInfo1]);
		case "Text":
			return rnd([icChanText1, icChanText2, icChanText3, icChanText4]);
		// TODO: case "Announcement":
		// TODO: case "DocumentComment":
		// TODO: case "DocumentBranch":
		default:
			return rnd([icChanUnknown]);
	}
};

export const ChannelIcon = (props: {
	channel: Channel;
	animate?: boolean;
	style?: string;
	extraSpacing?: boolean;
}) => {
	const api = useApi();
	const currentUser = useCurrentUser();
	const otherUser = createMemo(() => {
		if (props.channel.type === "Dm") {
			const selfId = currentUser()?.id;
			return props.channel.recipients?.find((i) => i.id !== selfId);
		}
		return undefined;
	});
	const typing = createMemo(() => {
		const other = otherUser();
		if (!other) return false;
		return api.typing.get(props.channel.id)?.has(other.id) ?? false;
	});

	return (
		<Switch>
			<Match when={props.channel.type === "Dm" && otherUser()}>
				<AvatarWithStatus
					user={otherUser()}
					animate={props.animate}
					typing={typing()}
					extraSpacing={props.extraSpacing}
				/>
			</Match>
			<Match when={props.channel.type === "Gdm"}>
				<ChannelIconGdm
					id={props.channel.id}
					icon={props.channel.icon}
					animate={props.animate}
				/>
			</Match>
			<Match when={true}>
				<ChannelIconRoom
					id={props.channel.id}
					type={props.channel.type}
					nsfw={props.channel.nsfw}
					animate={props.animate}
					style={props.style}
				/>
			</Match>
		</Switch>
	);
};

export const ChannelIconRoom = (props: {
	id: string;
	type: ChannelType;
	nsfw?: boolean;
	animate?: boolean;
	style?: string;
}) => {
	return (
		<svg
			aria-hidden="true"
			class="channel-icon"
			viewBox="0 0 64 64"
			style={props.style}
		>
			<defs>
				<mask id="nsfw">
					<rect width="64" height="64" x="0" y="0" fill="white" />
					<rect rx="4" width="32" height="32" x="32" y="0" fill="black" />
				</mask>
			</defs>
			<g mask={props.nsfw ? "url(#nsfw)" : undefined}>
				<rect
					width="64"
					height="64"
					x="0"
					y="0"
					class="inner"
					mask={`url(${channelIcon(props.type, props.id)})`}
				/>
			</g>

			<Show when={props.nsfw}>
				<rect
					width="64"
					height="64"
					x="0"
					y="0"
					class="inner"
					mask={`url(${icChanNsfw})`}
				/>
			</Show>
		</svg>
	);
};

export const ChannelIconGdm = (props: {
	id: string;
	icon?: string | null;
	pad?: number;
	animate?: boolean;
}) => {
	const pad = () => props.pad ?? 8;
	const size = 64;
	const totalSize = () => size + pad() * 2;
	return (
		<svg
			aria-hidden="true"
			class="avatar"
			viewBox={`0 0 ${totalSize()} ${totalSize()}`}
			role="img"
			style={{ "--pad": `${pad()}px` }}
		>
			<defs>
				<mask id="thread-icon-mask">
					<rect
						rx="6"
						width={size}
						height={size}
						x={pad()}
						y={pad()}
						fill="white"
					/>
				</mask>
			</defs>
			<g mask="url(#thread-icon-mask)">
				<rect
					width={size}
					height={size}
					x={pad()}
					y={pad()}
					fill={props.icon ? "oklch(var(--color-bg3))" : getColor(props.id)}
				/>
				<Show when={props.icon}>
					{(icon) => (
						<image
							preserveAspectRatio="xMidYMid slice"
							width={size}
							height={size}
							x={pad()}
							y={pad()}
							href={getThumbFromId(icon(), 64, props.animate)}
						/>
					)}
				</Show>
			</g>
		</svg>
	);
};
